import type { i18n } from 'i18next';
import { type ResponseContext } from '@linktivity/ars-neo';
import { message } from '@linktivity/link-ui';
import { AUTHORIZATION_HEADER, AGENT_HEADER } from '../constants';
import { buildLocalizedPath, buildLoginPathForCurrentPage } from '../utils';
import {
  ErrorID,
  isOtpError,
  isNotFoundApiRequest,
  COUPON_ERROR_ID,
  PROMOTION_ERROR_ID
} from './consts';

interface RequestHeadersOptions {
  getAccessToken: () => string | undefined;
  agentId: string;
}

export const createBuildRequestHeaders =
  ({ getAccessToken, agentId }: RequestHeadersOptions) =>
  (headers?: HeadersInit) => {
    const requestHeaders = new Headers(headers ?? {});
    if (!requestHeaders.has('Content-Type')) {
      requestHeaders.set('Content-Type', 'application/json');
    }
    const accessToken = getAccessToken();
    if (accessToken) {
      requestHeaders.set(AUTHORIZATION_HEADER, accessToken);
    }
    requestHeaders.set(AGENT_HEADER, agentId);
    return requestHeaders;
  };

interface PreMiddlewareAuth {
  isTokenExpiringSoon?: () => boolean;
  refreshExpiredToken: () => Promise<string | undefined>;
}

export const createPreMiddleware =
  (
    getAuth: () => PreMiddlewareAuth,
    buildRequestHeaders: (headers?: HeadersInit) => Headers
  ) =>
  async (context: { init: RequestInit }) => {
    const auth = getAuth();
    if (auth.isTokenExpiringSoon?.()) {
      auth.refreshExpiredToken().catch(() => {});
    }
    context.init.headers = buildRequestHeaders(context.init.headers);
  };

const REFRESH_TOKEN_PATH = '/v1/token/refresh';

interface UnauthorizedAuth {
  refreshExpiredToken: () => Promise<string | undefined>;
  clearUser: () => void;
}

const readResponseData = async (
  response: Response
): Promise<Record<string, unknown> | null> => {
  const clone = response.clone();
  const data = await clone.json().catch(() => null);
  return data && typeof data === 'object'
    ? (data as Record<string, unknown>)
    : null;
};

const readErrorId = async (response: Response): Promise<unknown> => {
  const data = await readResponseData(response);
  return data && 'error_id' in data ? data.error_id : undefined;
};

function getErrorMessage(resp: Record<string, unknown> | null): string {
  const desc =
    typeof resp?.error_description === 'string' ? resp.error_description : '';
  const match = desc.match(/message:\s*([\s\S]*)$/);
  return match?.[1]?.trim() || desc.trim();
}

export const createHandleUnauthorized =
  (getAuth: () => UnauthorizedAuth) =>
  async (context: ResponseContext): Promise<Response | void> => {
    const auth = getAuth();
    const errorId = await readErrorId(context.response);
    // The refresh call itself expiring must not await the refresh in flight,
    // which is this very request: that never settles.
    if (
      errorId === ErrorID.ExpiredToken &&
      !context.url.endsWith(REFRESH_TOKEN_PATH)
    ) {
      const accessToken = await auth.refreshExpiredToken();
      if (accessToken) {
        return context.fetch(context.url, context.init);
      }
    }
    auth.clearUser();
    location.assign(buildLoginPathForCurrentPage());
  };

export const createHandleGenericError =
  (i18nInstance: i18n) => async (context: ResponseContext) => {
    const data = await readResponseData(context.response);
    const errorId = data?.error_id;
    const isNotFoundError =
      errorId === ErrorID.ProductNotFound || errorId === ErrorID.NotFound;
    if (
      isNotFoundError &&
      isNotFoundApiRequest(context.response.url) &&
      location.pathname.includes('/product/')
    ) {
      if (!location.pathname.endsWith('not-found')) {
        location.replace(buildLocalizedPath('not-found'));
      }
      return data;
    }
    const i18nKey = `share.apiErrors.ARSError.${errorId}`;
    if (errorId && i18nInstance.exists(i18nKey)) {
      message.error(i18nInstance.t(i18nKey), 10);
    } else {
      message.error((data?.error_description || errorId) as string, 10);
    }
    return data;
  };

export const createHandleTooManyRequests =
  (i18nInstance: i18n) => async (context: ResponseContext) => {
    const errorId = await readErrorId(context.response);
    if (errorId === ErrorID.UserRateLimited) {
      if (!location.pathname.endsWith('too-many-requests')) {
        location.replace(buildLocalizedPath('too-many-requests'));
      }
    }
    if (errorId === ErrorID.BookingRateLimited) {
      message.error(
        i18nInstance.t('share.apiErrors.ARSError.BOOKING_RATE_LIMITED'),
        10
      );
    }
  };

export const createHandleInternalServerError =
  (i18nInstance: i18n) => async (context: ResponseContext) => {
    let shouldShowGenericServerError = true;

    try {
      const errorId = await readErrorId(context.response);
      if (errorId === ErrorID.LotteryAlreadyApplied) {
        message.error(
          i18nInstance.t('share.apiErrors.lottery.LOTTERY_ALREADY_APPLIED'),
          10
        );
        return;
      }

      if (
        typeof errorId === 'string' &&
        (errorId.includes(COUPON_ERROR_ID) ||
          errorId.includes(PROMOTION_ERROR_ID) ||
          isOtpError(errorId))
      ) {
        shouldShowGenericServerError = false;
      }
    } catch {
      shouldShowGenericServerError = true;
    }

    if (shouldShowGenericServerError) {
      const errorMessage = getErrorMessage(
        await readResponseData(context.response)
      );
      message.error(
        errorMessage || i18nInstance.t('share.apiErrors.serverError'),
        10
      );
    }
  };
